import { ReactNode, useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useAuthContext } from '@/contexts/AuthContext';
import { isAuthRequired } from '@/lib/environment';
import { AuthModal } from '@/components/AuthModal';
import { AppLoader } from '@/components/AppLoader';
import { WelcomeScreen } from '@/components/WelcomeScreen';
import { cn } from '@/lib/utils';

// Key for storing authorized team in localStorage (must match QRCodeLogin)
const DEVICE_AUTHORIZED_TEAM_KEY = 'team_v_authorized_team';

// Extend window type for initial loader
declare global {
  interface Window {
    hideInitialLoader?: () => void;
  }
}

interface AuthGateProps {
  children: ReactNode;
  /** Force require auth even in dev mode */
  forceAuth?: boolean;
  /** Custom loading component */
  loadingComponent?: ReactNode;
}

const isAuthDebugEnabled = () => {
  try {
    return sessionStorage.getItem('auth_gate_debug_enabled') === '1';
  } catch {
    return false;
  }
};

// Log for debugging auth flow (opt-in)
const logDebug = (message: string, data?: any) => {
  if (!isAuthDebugEnabled()) return;
  const timestamp = new Date().toISOString();
  const logEntry = `[AuthGate ${timestamp}] ${message}`;
  console.log(logEntry, data || '');

  // Store in sessionStorage for debugging
  try {
    const logs = JSON.parse(sessionStorage.getItem('auth_gate_debug') || '[]');
    logs.push({ timestamp, message, data });
    if (logs.length > 50) logs.shift();
    sessionStorage.setItem('auth_gate_debug', JSON.stringify(logs));
  } catch {
    // ignore storage errors
  }
};

/**
 * Check if device is authorized via QR code flow
 */
function isDeviceAuthorized(): { authorized: boolean; teamId: string | null } {
  try {
    const teamId = localStorage.getItem(DEVICE_AUTHORIZED_TEAM_KEY);
    return { authorized: !!teamId, teamId };
  } catch (e) {
    return { authorized: false, teamId: null };
  }
}

/**
 * AuthGate wraps content that requires authentication.
 * 
 * In development: Shows children without requiring login
 * In production: Shows login prompt if not authenticated
 * 
 * Also supports device authorization via QR code for shared devices (e.g., TVs)
 */
export function AuthGate({ children, forceAuth = false, loadingComponent }: AuthGateProps) {
  const { isAuthenticated, loading } = useAuthContext();
  const { teamSlug } = useParams<{ teamSlug: string }>();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'signup'>('login');
  const [contentReady, setContentReady] = useState(false);
  const [deviceAuth, setDeviceAuth] = useState<{ authorized: boolean; teamId: string | null }>({ authorized: false, teamId: null });
  const [devBypassLoading, setDevBypassLoading] = useState(false);

  const requireAuth = forceAuth || isAuthRequired();

  // Optimistic auth: if a stored session exists, assume authenticated and show
  // content immediately. Auth will restore in the background — if it fails,
  // the user will be redirected to login at that point.
  const hasStoredSession = useMemo(() => {
    try {
      const storageKey = `sb-${import.meta.env.VITE_SUPABASE_PROJECT_ID || 'gipteowdczmwaskrqunq'}-auth-token`;
      return !!localStorage.getItem(storageKey);
    } catch {
      return false;
    }
  }, []);
  const optimisticAuth = hasStoredSession && loading;

  // Check device authorization on mount
  useEffect(() => {
    const authStatus = isDeviceAuthorized();
    logDebug('Device authorization check', authStatus);
    setDeviceAuth(authStatus);
  }, []);

  // Hide the initial HTML loader once React takes over
  useEffect(() => {
    if (typeof window !== 'undefined' && window.hideInitialLoader) {
      // Small delay to ensure smooth transition
      const timer = setTimeout(() => {
        window.hideInitialLoader?.();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, []);

  // Show shell immediately to avoid white flashes.
  useEffect(() => {
    setContentReady(true);
  }, []);

  // Trigger content fade-in after auth resolves
  useEffect(() => {
    if (!loading) {
      logDebug('Auth loading complete', { isAuthenticated, deviceAuth });
      return undefined;
    }
  }, [loading, isAuthenticated, deviceAuth]);

  // Never block the app indefinitely if auth loading gets stuck.
  // Dev: bypass immediately. Production: allow a short grace period, then
  // always unblock UI (session restore can still complete in background).
  useEffect(() => {
    if (!requireAuth) {
      setDevBypassLoading(true);
      return;
    }

    if (loading) {
      // If we have a stored session, bypass immediately (optimistic auth)
      if (hasStoredSession) {
        setDevBypassLoading(true);
        return;
      }

      // No stored session: short grace period (1s) then show login
      const timer = setTimeout(() => {
        console.warn('[AuthGate] No stored session and auth loading timed out, showing login');
        setDevBypassLoading(true);
      }, 1000);

      return () => clearTimeout(timer);
    }

    setDevBypassLoading(false);
  }, [loading, requireAuth]);

  // If we bypass loading, ensure content becomes visible.
  useEffect(() => {
    if (devBypassLoading) {
      setContentReady(true);
    }
  }, [devBypassLoading]);

  // Handle QR authorization callback
  const handleQRAuthorized = (teamId: string) => {
    logDebug('QR authorization callback received', { teamId });
    setDeviceAuth({ authorized: true, teamId });
    setShowAuthModal(false);

    // Force a full page reload so that all contexts and providers re-initialize
    // with the completed device authorization.
    window.location.reload();
  };

  // In dev mode without forceAuth, always show children
  if (!requireAuth) {
    logDebug('Dev mode - showing children without auth');
    return (
      <div className={cn(
        "transition-opacity duration-300",
        contentReady || devBypassLoading ? "opacity-100" : "opacity-0"
      )}>
        {children}
      </div>
    );
  }

  // Check if user is authenticated OR device is authorized
  // Check if user is authenticated OR device is authorized OR optimistically assumed
  const hasAccess = isAuthenticated || deviceAuth.authorized || optimisticAuth;

  logDebug('Access check', { isAuthenticated, deviceAuthorized: deviceAuth.authorized, optimisticAuth, hasAccess });

  // Show loader only if no stored session and auth is still loading
  if (loading && !devBypassLoading && !optimisticAuth) {
    return loadingComponent || <AppLoader isLoading={true} message="Controleren..." minDisplayTime={0} />;
  }

  // In production (or forceAuth), require authentication or device authorization
  if (!hasAccess) {
    return (
      <>
        <WelcomeScreen
          onLogin={() => {
            setAuthMode('login');
            setShowAuthModal(true);
          }}
          onSignup={() => {
            setAuthMode('signup');
            setShowAuthModal(true);
          }}
          teamName={teamSlug}
        />

        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          defaultMode={authMode}
          onQRAuthorized={handleQRAuthorized}
        />
      </>
    );
  }

  return (
    <div className={cn(
      "transition-opacity duration-300",
      "opacity-100"
    )}>
      {children}
    </div>
  );
}
